import { useState, useEffect, useRef, useCallback } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { ArrowLeft, Volume2, ChevronLeft, ChevronRight, BookOpen, Lightbulb, MessageSquare, Eraser, LogOut, Download, X } from 'lucide-react'
import { vocabulary } from '../data/vocabulary'
import { Vocabulary as VocabType, Category } from '../data/types'
import { categories } from '../data/categories'
import { getEmojiUrl, getWordImageUrl } from '../utils/wordImages'

// Extended vocabulary with learning data
const vocabularyWithLearning = vocabulary.map(word => ({
  ...word,
  example: word.example || getDefaultExample(word.chinese, word.english),
  exampleEnglish: word.exampleEnglish || getDefaultExampleEnglish(word.chinese, word.english),
  memoryTip: word.memoryTip || getMemoryTip(word.chinese, word.english),
  parts: word.parts || getParts(word.chinese),
}))

function getDefaultExample(chinese: string, _english: string): string {
  const examples: Record<string, string> = {
    // Greetings
    '你好': '你好，很高兴认识你。',
    '再见': '我们要走了，再见！',
    '谢谢': '谢谢你的帮助。',
    '不客气': '不客气，别客气！',
    '对不起': '对不起，我迟到了。',
    '没关系': '没关系，别担心。',
    '请': '请坐！',
    '早上': '早上好！你吃早饭了吗？',
    '晚上': '晚上我要看电视。',
    '欢迎': '欢迎来我家玩！',
    '认识': '我很高兴认识你。',
    '高兴': '我今天很高兴！',
    '名字': '你叫什么名字？',
    '先生': '王先生好！',
    '小姐': '李小姐很漂亮。',
    // Numbers
    '一': '我有一个苹果。',
    '二': '我们有两个孩子。',
    '三': '今天是三月三号。',
    '四': '我家有四口人。',
    '五': '我五点放学。',
    '六': '我今年六岁。',
    '七': '一个星期有七天。',
    '八': '我八点上学。',
    '九': '九点了，该睡觉了。',
    '十': '我有十个手指。',
    '百': '一百个学生。',
    '千': '一千块钱。',
    '万': '一万人参加比赛。',
    '零': '零度很冷。',
    '两': '我要两个包子。',
    '第一': '他是第一名！',
    '第二': '我跑了第二名。',
    '多少': '这个多少钱？',
    '几': '你几岁了？',
    '半': '我等了半个小时。',
    '双': '我买了一双鞋。',
    '个': '我吃了一个苹果。',
    '岁': '你几岁了？',
    '元': '一瓶水两元。',
    '块': '这个五块钱。',
    // Family
    '爸爸': '我爸爸是老师。',
    '妈妈': '妈妈在做饭。',
    '哥哥': '我哥哥比我大三岁。',
    '姐姐': '姐姐在大学读书。',
    '弟弟': '弟弟在写作业。',
    '妹妹': '妹妹喜欢画画。',
    '爷爷': '爷爷给我讲故事。',
    '奶奶': '奶奶做的饭最好吃！',
    '叔叔': '叔叔带我去公园。',
    '阿姨': '阿姨给我买了新衣服。',
    '儿子': '他是我的小儿子。',
    '女儿': '我的女儿很可爱。',
    '孩子': '孩子们在玩。',
    '家': '我下午三点回家。',
    '外公': '外公住在乡下。',
    '外婆': '外婆做的饺子最好吃。',
    '朋友': '他是我的好朋友。',
    '家人': '我爱我的家人。',
    '邻居': '邻居家的狗很可爱。',
    '人': '这里人很多。',
    '丈夫': '她的丈夫是医生。',
    '妻子': '他的妻子很温柔。',
    '男人': '那个男人在跑步。',
    '女人': '那个女人在看书。',
    '我': '我是学生。',
    '你': '你喜欢什么颜色？',
    '您': '您贵姓？',
    '他': '他是我的同学。',
    '她': '她唱歌很好听。',
    '它': '它是一只小猫。',
    '我们': '我们一起去玩吧！',
    '大家': '大家一起来！',
    // Food
    '饭': '我们吃饭吧！',
    '米饭': '我想吃米饭。',
    '面条': '妈妈做了面条。',
    '饺子': '过年吃饺子。',
    '面包': '我早饭吃面包。',
    '鸡蛋': '我要一个鸡蛋。',
    '肉': '我不太喜欢吃肉。',
    '牛肉': '牛肉面很好吃。',
    '鸡肉': '今天吃鸡肉。',
    '鱼': '这条鱼很新鲜。',
    '菜': '多吃菜对身体好。',
    '水果': '我喜欢吃水果。',
    '苹果': '我每天吃一个苹果。',
    '香蕉': '猴子喜欢吃香蕉。',
    '西瓜': '夏天吃西瓜最开心！',
    '茶': '爷爷喜欢喝茶。',
    '咖啡': '妈妈每天喝咖啡。',
    '水': '请给我一杯水。',
    '牛奶': '我每天喝牛奶。',
    '果汁': '我要一杯果汁。',
    '可乐': '小朋友少喝可乐。',
    '好吃': '这个蛋糕真好吃！',
    '甜': '这个糖很甜。',
    '酸': '这个柠檬很酸。',
    '辣': '我不喜欢吃辣的。',
    '咸': '这个菜太咸了。',
    '饿': '我饿了，想吃东西。',
    '渴': '我渴了，想喝水。',
    '吃': '我们在吃晚饭。',
    '喝': '你想喝什么？',
    '羊肉': '羊肉串很好吃。',
    // Animals
    '狗': '我家有一只小狗。',
    '猫': '猫喜欢睡觉。',
    '鸟': '树上有一只小鸟。',
    '马': '马跑得很快。',
    '牛': '牛在草地上吃草。',
    '羊': '小羊在吃奶。',
    '猪': '猪喜欢吃东西。',
    '鸡': '公鸡早上会叫。',
    '鸭': '鸭子在水里游。',
    '兔': '小兔子很可爱。',
    '老虎': '老虎是森林之王。',
    '大象': '大象的鼻子很长。',
    '猴子': '猴子喜欢爬树。',
    '蛇': '蛇没有脚。',
    '虫': '花上有一只小虫。',
    '熊猫': '熊猫喜欢吃竹子。',
    '动物': '我喜欢小动物。',
    '宠物': '我想养一个宠物。',
    '龙': '龙是中国人的象征。',
    // Colors
    '颜色': '你最喜欢什么颜色？',
    '红': '这个苹果是红色的。',
    '蓝': '天空是蓝色的。',
    '绿': '草是绿色的。',
    '黄': '香蕉是黄色的。',
    '白': '雪是白色的。',
    '黑': '天黑了，该回家了。',
    '橙': '橘子是橙色的。',
    '紫': '葡萄是紫色的。',
    '粉红': '她穿了一件粉红色的裙子。',
    '灰': '大象是灰色的。',
    '棕色': '树干是棕色的。',
    '金色': '太阳发出金色的光。',
    '银色': '月亮是银色的。',
    '深': '这条河很深。',
    // Body
    '身体': '你要锻炼身体。',
    '头': '我的头疼。',
    '眼睛': '保护好你的眼睛。',
    '耳朵': '兔子的耳朵很长。',
    '鼻子': '大象的鼻子很长。',
    '嘴': '闭上嘴吃饭。',
    '手': '洗洗手再吃饭。',
    '脚': '我的脚好疼。',
    '腿': '他跑得快，腿很长。',
    '胳膊': '我的胳膊受伤了。',
    '头发': '她的头发很长。',
    '脸': '洗洗脸吧。',
    '牙': '每天要刷牙。',
    '心': '谢谢你的好心。',
    '生病': '我生病了，不能去学校。',
    // Time
    '时间': '时间过得真快！',
    '今天': '今天天气很好。',
    '明天': '明天见！',
    '昨天': '昨天我去了公园。',
    '现在': '现在几点了？',
    '以前': '我以前住在北京。',
    '以后': '以后你要好好学习。',
    '年': '今年是2025年。',
    '月': '一个月有三十天。',
    '日': '今天是几号？',
    '星期': '一个星期有七天。',
    '周末': '周末我们去公园玩。',
    '点': '现在八点了。',
    '分': '还有五分钟。',
    '秒': '一秒很快。',
    '小时': '我等了一个小时。',
    '开始': '比赛开始了！',
    '结束': '电影结束了。',
    '快': '他跑得真快！',
    '慢': '请慢一点走。',
    '上午': '上午有四节课。',
    '中午': '中午我在学校吃饭。',
    '下午': '下午放学后我去打球。',
    '时候': '小时候我很调皮。',
    '分钟': '等我五分钟。',
    '去年': '去年我去了北京。',
    '明年': '明年我要学游泳。',
    '号': '今天是几号？',
    // Weather
    '天气': '今天天气怎么样？',
    '晴天': '晴天，我们去公园吧！',
    '阴天': '阴天了，可能要下雨。',
    '下雨': '外面下雨了，带伞！',
    '下雪': '下雪了！我们去堆雪人吧！',
    '风': '今天风很大。',
    '云': '天上有很多云。',
    '太阳': '太阳出来了，好暖和！',
    '月亮': '月亮又大又圆。',
    '星星': '晚上能看到很多星星。',
    '热': '今天太热了！',
    '冷': '冬天很冷，多穿衣服。',
    '暖': '春天很暖和。',
    '凉': '秋天天气凉了。',
    '温度': '今天的温度是多少？',
    '雪': '雪是白色的。',
    '晴': '今天天气晴。',
    '阴': '天阴了。',
    // School
    '学校': '我在学校学习中文。',
    '老师': '我的老师很和蔼。',
    '学生': '他是我的学生。',
    '同学': '我和同学一起玩。',
    '教室': '教室里很安静。',
    '书': '我在看一本有趣的书。',
    '本子': '请拿一个本子出来。',
    '笔': '请用笔写。',
    '字典': '不会的字查字典。',
    '电脑': '我在用电脑写作业。',
    '作业': '我还没写完作业。',
    '考试': '明天有考试。',
    '问题': '我有一个问题。',
    '回答': '请回答这个问题。',
    '学习': '我每天学习中文。',
    '教': '妈妈教我做菜。',
    '懂': '你听懂了吗？',
    '容易': '这道题很容易。',
    '难': '这道题太难了！',
    '练习': '每天要练习写字。',
    '字': '这个字怎么读？',
    '汉语': '我在学汉语。',
    '课': '今天有三节课。',
    '题': '这道题我不会。',
    // Places
    '地方': '这个地方真漂亮！',
    '厕所': '我要去厕所。',
    '房间': '我的房间很干净。',
    '公司': '爸爸去公司上班了。',
    '商店': '我去商店买文具。',
    '超市': '妈妈在超市买菜。',
    '医院': '我生病了要去医院。',
    '银行': '妈妈去银行取钱。',
    '饭店': '我们去饭店吃饭吧。',
    '机场': '爸爸去机场接朋友。',
    '车站': '我在车站等车。',
    '公园': '周末我去公园玩。',
    '图书馆': '我在图书馆看书。',
    '办公室': '老师在办公室里。',
    '城市': '上海是一个大城市。',
    '国家': '中国是一个很大的国家。',
    '中国': '我住在中国。',
    '北京': '北京是中国的首都。',
    '上海': '上海有很多高楼。',
    '泰国': '泰国很热。',
    '上': '书在桌子上。',
    '这': '这是我的书包。',
    '这儿': '我就在这儿。',
    '那': '那是谁的球？',
    '那儿': '你去那儿干什么？',
    '哪': '哪一个是你的？',
    '哪儿': '你去哪儿？',
    // Verbs
    '做': '你在做什么？',
    '看': '我在看电视。',
    '听': '请听老师说话。',
    '说': '请慢慢说。',
    '写': '请把名字写在这里。',
    '读': '请大声读出来。',
    '走': '我们走路去吧。',
    '跑': '快跑！要迟到了！',
    '坐': '请坐下来。',
    '站': '请大家站起来。',
    '睡': '我十点睡觉。',
    '起': '早上几点起床？',
    '开': '请开门。',
    '关': '关灯睡觉。',
    '买': '我去买水果。',
    '卖': '这个商店卖文具。',
    '送': '我送你一个礼物。',
    '拿': '请把书拿给我。',
    '放': '把书包放在桌子上。',
    '找': '我在找我的笔。',
    '等': '等我一下！',
    '玩': '我们去玩吧！',
    '工作': '爸爸每天去工作。',
    '休息': '下课了，休息一下。',
    '帮助': '我帮助你吧。',
    '告诉': '我告诉你一个秘密。',
    '知道': '我知道答案了！',
    '会': '你会游泳吗？',
    '想': '我想吃冰淇淋。',
    '爱': '我爱我的家人。',
    '喜欢': '我喜欢画画。',
    '要': '我要一杯水。',
    '去': '我们去学校吧。',
    '来': '快来！',
    '住': '我住在北京。',
    '游泳': '夏天我喜欢游泳。',
    '飞': '鸟在天上飞。',
    '是': '他是我的朋友。',
    '有': '我有一只猫。',
    '在': '老师在教室里。',
    '看见': '我看见了一只小鸟。',
    '说话': '上课不要说话。',
    '问': '有问题就问老师。',
    '叫': '我叫小明。',
    '笑': '大家都在笑。',
    '穿': '今天穿厚一点。',
    '回': '我回家了。',
    '进': '请进！',
    '出': '他出去了。',
    '到': '我们到学校了。',
    '给': '请给我一支笔。',
    '介绍': '我来介绍一下。',
    '让': '让我想想。',
    '希望': '我希望明天是晴天。',
    '完': '我写完作业了！',
    '准备': '你准备好了吗？',
    '能': '你能帮我吗？',
    '可以': '我可以去玩吗？',
    // Adjectives
    '大': '这个西瓜真大！',
    '小': '这只猫好小。',
    '多': '这里人好多。',
    '少': '我的钱很少。',
    '新': '我买了一个新书包。',
    '旧': '这件衣服太旧了。',
    '好': '今天天气真好！',
    '坏': '这个苹果坏了。',
    '对': '你说得对！',
    '错': '对不起，我做错了。',
    '忙': '妈妈今天很忙。',
    '累': '跑完步我很累。',
    '便宜': '这个很便宜。',
    '贵': '这个太贵了！',
    '远': '学校离家很远。',
    '近': '公园离我家很近。',
    '高': '那棵树好高！',
    '矮': '我比哥哥矮。',
    '长': '这条路很长。',
    '短': '我的铅笔太短了。',
    '宽': '这条路很宽。',
    '窄': '这条巷子很窄。',
    '干净': '你的房间很干净。',
    '脏': '你的手好脏，快去洗！',
    '漂亮': '这朵花真漂亮！',
    '好看': '这件衣服真好看。',
    '快乐': '祝你生日快乐！',
    // Daily
    '网络': '我家有网络。',
    '手机': '不要一直看手机。',
    '电视': '我在看电视。',
    '电话': '妈妈在打电话。',
    '衣服': '今天穿这件衣服。',
    '裤子': '我买了一条新裤子。',
    '鞋': '把鞋穿好。',
    '桌子': '把东西放在桌子上。',
    '椅子': '请坐在椅子上。',
    '床': '我九点上床睡觉。',
    '门': '请关门。',
    '窗': '请打开窗户。',
    '车': '爸爸开车送我去学校。',
    '出租车': '我们坐出租车去吧。',
    '飞机': '我坐过飞机。',
    '票': '我买了电影票。',
    '钱': '我存了很多钱。',
    '礼物': '这是给你的生日礼物。',
    '事情': '我有事情要做。',
    '意思': '这个字是什么意思？',
    '旁边': '学校旁边有个超市。',
    '前面': '我家前面有个公园。',
    '后面': '猫躲在沙发后面。',
    '左边': '学校在左边。',
    '右边': '医院在右边。',
    '睡觉': '我九点睡觉。',
    '起床': '我每天六点起床。',
    '上班': '爸爸去上班了。',
    '洗': '我洗了手。',
    '打电话': '我给妈妈打电话。',
    '跑步': '我每天早上跑步。',
    '踢足球': '放学后我们踢足球。',
    '打篮球': '我喜欢打篮球。',
    '运动': '每天要运动。',
    '跳舞': '姐姐喜欢跳舞。',
    '唱歌': '我喜欢唱歌。',
    '旅游': '放假我们去旅游。',
    '医生': '我想当医生。',
    '服务员': '服务员，请给我菜单。',
    '东西': '我的东西呢？',
    '电影': '我们去看电影吧！',
    '公共汽车': '我坐公共汽车上学。',
    '火车': '我坐过火车。',
    '自行车': '我会骑自行车。',
    '船': '我们坐船过河。',
    '报纸': '爷爷在看报纸。',
    '药': '生病了要吃药。',
    '杯子': '请给我一个杯子。',
    '生日': '今天是我的生日！',
    '谁': '谁在门口？',
    '什么': '你在做什么？',
    '怎么': '这个字怎么写？',
    '怎么样': '你最近怎么样？',
    '为什么': '为什么天是蓝的？',
    '和': '我和朋友一起去。',
    '但是': '我想去，但是下雨了。',
    '因为': '因为下雨了，所以不去。',
    '所以': '太累了，所以休息一下。',
    '从': '我从家走到学校。',
    '向': '向前走一百米。',
    '离': '学校离家不远。',
    '比': '我比弟弟高。',
    '很': '今天很冷。',
    '非常': '我非常开心！',
    '太': '太好了！',
    '最': '这是我最喜欢的书。',
    '真': '你真棒！',
    '都': '我们都是好朋友。',
    '不': '我不知道。',
    '没': '我没带伞。',
    '每': '每天都要开心。',
    '也': '我也想去。',
    '还': '我还没吃饭。',
    '再': '再见！下次再来。',
    '就': '就来！',
    '别': '别跑太快！',
    '已经': '我已经写完了。',
    '一起': '我们一起玩吧！',
    '可能': '明天可能下雨。',
    '正在': '我正在写作业。',
    '一点': '请慢一点。',
    '的': '这是我的书。',
    '得': '他跑得很快。',
    '了': '我吃了饭了。',
    '着': '他看着书。',
    '过': '我去过北京。',
    '吗': '你是学生吗？',
    '呢': '你呢？',
    '吧': '走吧！',
    // Appended
    '喂': '喂，你好！',
    '本': '这本书很有趣。',
    '件': '我买了一件衣服。',
    '张': '请给我一张纸。',
    '次': '这是第一次来。',
    '公斤': '我买了两公斤苹果。',
    '些': '给我一些水果。',
  }
  return examples[chinese] || `我喜欢${chinese}。`
}

function getDefaultExampleEnglish(chinese: string, english: string): string {
  const examples: Record<string, string> = {
    // Greetings
    '你好': 'Hello, nice to meet you.',
    '再见': "We're leaving now, goodbye!",
    '谢谢': 'Thank you for your help.',
    '不客气': "You're welcome! I'm happy to help.",
    '对不起': "Sorry, I'm late.",
    '没关系': "It's okay, don't worry.",
    '请': 'Please sit down!',
    '早上': 'Good morning! Have you had breakfast?',
    '晚上': "I'm going to watch TV in the evening.",
    '欢迎': 'Welcome to my house!',
    '认识': "I'm glad to know you.",
    '高兴': "I'm very happy today!",
    '名字': 'What is your name?',
    '先生': 'Hello, Mr. Wang!',
    '小姐': 'Miss Li is very pretty.',
    // Numbers
    '一': 'I have one apple.',
    '二': 'We have two children.',
    '三': "Today is March 3rd.",
    '四': 'There are four people in my family.',
    '五': 'I finish school at five.',
    '六': 'I am six years old.',
    '七': 'There are seven days in a week.',
    '八': 'I go to school at eight.',
    '九': "It's nine o'clock, time for bed.",
    '十': 'I have ten fingers.',
    '百': 'There are one hundred students.',
    '千': 'It costs one thousand yuan.',
    '万': 'Ten thousand people joined the race.',
    '零': 'Zero degrees is very cold.',
    '两': 'I want two buns, please.',
    '第一': 'He got first place!',
    '第二': 'I came in second.',
    '多少': 'How much is this?',
    '几': 'How old are you?',
    '半': 'I waited for half an hour.',
    '双': 'I bought a pair of shoes.',
    '个': 'I ate an apple.',
    '岁': 'How old are you?',
    '元': 'A bottle of water is two yuan.',
    '块': 'This costs five kuai.',
    // Family
    '爸爸': 'My father is a teacher.',
    '妈妈': 'Mom is cooking.',
    '哥哥': 'My older brother is three years older than me.',
    '姐姐': 'My older sister is studying at university.',
    '弟弟': 'My little brother is doing homework.',
    '妹妹': 'My little sister loves drawing.',
    '爷爷': 'Grandpa tells me stories.',
    '奶奶': "Grandma's cooking is the best!",
    '叔叔': 'Uncle took me to the park.',
    '阿姨': 'Auntie bought me new clothes.',
    '儿子': 'He is my little son.',
    '女儿': 'My daughter is very cute.',
    '孩子': 'The children are playing.',
    '家': 'I go home at three in the afternoon.',
    '外公': 'Grandpa lives in the countryside.',
    '外婆': 'Grandma makes the best dumplings.',
    '朋友': 'He is my good friend.',
    '家人': 'I love my family.',
    '邻居': "The neighbor's dog is very cute.",
    '人': 'There are a lot of people here.',
    '丈夫': 'Her husband is a doctor.',
    '妻子': 'His wife is very kind.',
    '男人': 'That man is running.',
    '女人': 'That woman is reading a book.',
    '我': 'I am a student.',
    '你': 'What color do you like?',
    '您': 'May I ask your name?',
    '他': 'He is my classmate.',
    '她': 'She sings beautifully.',
    '它': 'It is a small cat.',
    '我们': "Let's go together!",
    '大家': 'Everyone, come on!',
    // Food
    '饭': "Let's eat!",
    '米饭': 'I want to eat rice.',
    '面条': 'Mom made noodles.',
    '饺子': 'We eat dumplings for New Year.',
    '面包': 'I have bread for breakfast.',
    '鸡蛋': 'I want one egg, please.',
    '肉': "I don't really like eating meat.",
    '牛肉': 'Beef noodles are delicious.',
    '鸡肉': "We're having chicken today.",
    '鱼': 'This fish is very fresh.',
    '菜': 'Eating vegetables is good for you.',
    '水果': 'I like eating fruit.',
    '苹果': 'I eat an apple every day.',
    '香蕉': 'Monkeys like bananas.',
    '西瓜': 'Eating watermelon in summer is the best!',
    '茶': 'Grandpa likes drinking tea.',
    '咖啡': 'Mom drinks coffee every day.',
    '水': 'Please give me a glass of water.',
    '牛奶': 'I drink milk every day.',
    '果汁': 'I want a glass of juice.',
    '可乐': 'Kids should drink less cola.',
    '好吃': 'This cake is so yummy!',
    '甜': 'This candy is very sweet.',
    '酸': 'This lemon is very sour.',
    '辣': "I don't like spicy food.",
    '咸': 'This dish is too salty.',
    '饿': "I'm hungry, I want to eat.",
    '渴': "I'm thirsty, I want some water.",
    '吃': "We're having dinner.",
    '喝': 'What do you want to drink?',
    '羊肉': 'Lamb skewers are delicious.',
    // Animals
    '狗': 'My family has a small dog.',
    '猫': 'Cats like to sleep.',
    '鸟': 'There is a little bird on the tree.',
    '马': 'Horses run very fast.',
    '牛': 'Cows are eating grass in the field.',
    '羊': 'The little sheep is drinking milk.',
    '猪': 'Pigs love to eat.',
    '鸡': 'The rooster crows in the morning.',
    '鸭': 'Ducks swim in the water.',
    '兔': 'The little rabbit is so cute.',
    '老虎': 'The tiger is the king of the forest.',
    '大象': 'The elephant has a long trunk.',
    '猴子': 'Monkeys love climbing trees.',
    '蛇': 'Snakes have no legs.',
    '虫': 'There is a little bug on the flower.',
    '熊猫': 'Pandas love eating bamboo.',
    '动物': 'I like animals.',
    '宠物': 'I want to have a pet.',
    '龙': 'The dragon is a symbol of China.',
    // Colors
    '颜色': 'What is your favorite color?',
    '红': 'This apple is red.',
    '蓝': 'The sky is blue.',
    '绿': 'The grass is green.',
    '黄': 'Bananas are yellow.',
    '白': 'Snow is white.',
    '黑': "It's getting dark, time to go home.",
    '橙': 'Oranges are orange.',
    '紫': 'Grapes are purple.',
    '粉红': 'She is wearing a pink dress.',
    '灰': 'Elephants are gray.',
    '棕色': 'Tree trunks are brown.',
    '金色': 'The sun shines golden light.',
    '银色': 'The moon is silver.',
    '深': 'This river is very deep.',
    // Body
    '身体': 'You should exercise your body.',
    '头': 'My head hurts.',
    '眼睛': 'Take good care of your eyes.',
    '耳朵': "The rabbit's ears are very long.",
    '鼻子': "The elephant's trunk is very long.",
    '嘴': 'Close your mouth when you eat.',
    '手': 'Wash your hands before eating.',
    '脚': 'My foot hurts.',
    '腿': 'He runs fast because he has long legs.',
    '胳膊': 'I hurt my arm.',
    '头发': 'Her hair is very long.',
    '脸': 'Go wash your face.',
    '牙': 'Brush your teeth every day.',
    '心': 'Thank you for your kind heart.',
    '生病': "I'm sick, I can't go to school.",
    // Time
    '时间': 'Time flies so fast!',
    '今天': 'The weather is nice today.',
    '明天': 'See you tomorrow!',
    '昨天': 'I went to the park yesterday.',
    '现在': 'What time is it now?',
    '以前': 'I used to live in Beijing.',
    '以后': 'You should study hard from now on.',
    '年': 'This year is 2025.',
    '月': 'A month has thirty days.',
    '日': 'What is the date today?',
    '星期': 'A week has seven days.',
    '周末': "Let's go to the park on the weekend.",
    '点': "It's eight o'clock now.",
    '分': 'There are five minutes left.',
    '秒': 'One second is very fast.',
    '小时': 'I waited for one hour.',
    '开始': 'The game has started!',
    '结束': 'The movie is over.',
    '快': 'He runs so fast!',
    '慢': 'Please walk slowly.',
    '上午': 'There are four classes in the morning.',
    '中午': 'I eat lunch at school at noon.',
    '下午': 'I play ball after school in the afternoon.',
    '时候': 'I was naughty when I was little.',
    '分钟': 'Wait for me five minutes.',
    '去年': 'I went to Beijing last year.',
    '明年': "I'm going to learn swimming next year.",
    '号': 'What is the date today?',
    // Weather
    '天气': "How's the weather today?",
    '晴天': "It's sunny! Let's go to the park!",
    '阴天': "It's cloudy, it might rain.",
    '下雨': "It's raining outside, bring an umbrella!",
    '下雪': "It's snowing! Let's build a snowman!",
    '风': 'The wind is very strong today.',
    '云': 'There are many clouds in the sky.',
    '太阳': 'The sun is out, so warm!',
    '月亮': 'The moon is big and round.',
    '星星': 'You can see many stars at night.',
    '热': "It's so hot today!",
    '冷': "It's cold in winter, wear more clothes.",
    '暖': 'Spring is warm.',
    '凉': "It's getting cool in autumn.",
    '温度': 'What is the temperature today?',
    '雪': 'Snow is white.',
    '晴': "It's sunny today.",
    '阴': "It's cloudy.",
    // School
    '学校': 'I study Chinese at school.',
    '老师': 'My teacher is very kind.',
    '学生': 'He is my student.',
    '同学': 'I play with my classmates.',
    '教室': 'The classroom is very quiet.',
    '书': "I'm reading an interesting book.",
    '本子': 'Please take out a notebook.',
    '笔': 'Please write with a pen.',
    '字典': "Look up words you don't know in the dictionary.",
    '电脑': "I'm doing homework on the computer.",
    '作业': "I haven't finished my homework yet.",
    '考试': 'There is an exam tomorrow.',
    '问题': 'I have a question.',
    '回答': 'Please answer this question.',
    '学习': 'I study Chinese every day.',
    '教': 'Mom teaches me how to cook.',
    '懂': 'Did you understand?',
    '容易': 'This question is easy.',
    '难': 'This question is too hard!',
    '练习': 'You need to practice writing every day.',
    '字': 'How do you read this character?',
    '汉语': "I'm learning Chinese.",
    '课': 'There are three classes today.',
    '题': "I can't do this problem.",
    // Places
    '地方': 'This place is so beautiful!',
    '厕所': 'I need to go to the bathroom.',
    '房间': 'My room is very clean.',
    '公司': 'Dad went to the office.',
    '商店': 'I go to the shop to buy stationery.',
    '超市': 'Mom is buying vegetables at the supermarket.',
    '医院': "I'm sick, I need to go to the hospital.",
    '银行': 'Mom went to the bank to get money.',
    '饭店': "Let's eat at the restaurant.",
    '机场': 'Dad went to the airport to pick up a friend.',
    '车站': "I'm waiting at the station.",
    '公园': "I play in the park on weekends.",
    '图书馆': "I'm reading at the library.",
    '办公室': 'The teacher is in the office.',
    '城市': 'Shanghai is a big city.',
    '国家': 'China is a very big country.',
    '中国': 'I live in China.',
    '北京': 'Beijing is the capital of China.',
    '上海': 'Shanghai has many tall buildings.',
    '泰国': 'Thailand is very hot.',
    '上': 'The book is on the table.',
    '这': 'This is my backpack.',
    '这儿': "I'm right here.",
    '那': 'Whose ball is that?',
    '那儿': 'Why are you going there?',
    '哪': 'Which one is yours?',
    '哪儿': 'Where are you going?',
    // Verbs
    '做': 'What are you doing?',
    '看': "I'm watching TV.",
    '听': 'Please listen to the teacher.',
    '说': 'Please speak slowly.',
    '写': 'Please write your name here.',
    '读': 'Please read out loud.',
    '走': "Let's walk there.",
    '跑': "Run! We're going to be late!",
    '坐': 'Please sit down.',
    '站': 'Everyone, please stand up.',
    '睡': 'I go to bed at ten.',
    '起': 'What time do you get up?',
    '开': 'Please open the door.',
    '关': 'Turn off the light before sleeping.',
    '买': "I'm going to buy some fruit.",
    '卖': 'This shop sells stationery.',
    '送': 'I have a gift for you.',
    '拿': 'Please hand me the book.',
    '放': 'Put your bag on the table.',
    '找': "I'm looking for my pen.",
    '等': 'Wait for me!',
    '玩': "Let's go play!",
    '工作': 'Dad goes to work every day.',
    '休息': "Class is over, let's take a break.",
    '帮助': 'Let me help you.',
    '告诉': "I'll tell you a secret.",
    '知道': 'I know the answer!',
    '会': 'Can you swim?',
    '想': 'I want ice cream.',
    '爱': 'I love my family.',
    '喜欢': 'I like drawing.',
    '要': 'I want a glass of water.',
    '去': "Let's go to school.",
    '来': 'Come here!',
    '住': 'I live in Beijing.',
    '游泳': 'I like swimming in summer.',
    '飞': 'Birds fly in the sky.',
    '是': 'He is my friend.',
    '有': 'I have a cat.',
    '在': 'The teacher is in the classroom.',
    '看见': 'I saw a little bird.',
    '说话': "Don't talk in class.",
    '问': 'Ask the teacher if you have questions.',
    '叫': 'My name is Xiao Ming.',
    '笑': 'Everyone is laughing.',
    '穿': 'Wear something warm today.',
    '回': "I'm going home.",
    '进': 'Come in, please!',
    '出': 'He went out.',
    '到': "We've arrived at school.",
    '给': 'Please give me a pen.',
    '介绍': 'Let me introduce myself.',
    '让': 'Let me think.',
    '希望': 'I hope tomorrow is sunny.',
    '完': "I've finished my homework!",
    '准备': 'Are you ready?',
    '能': 'Can you help me?',
    '可以': 'Can I go play?',
    // Adjectives
    '大': 'This watermelon is so big!',
    '小': 'This cat is so small.',
    '多': 'There are so many people here.',
    '少': "I don't have much money.",
    '新': 'I bought a new backpack.',
    '旧': 'These clothes are too old.',
    '好': 'The weather is really nice today!',
    '坏': 'This apple has gone bad.',
    '对': "You're right!",
    '错': "Sorry, I made a mistake.",
    '忙': 'Mom is very busy today.',
    '累': "I'm tired after running.",
    '便宜': 'This is very cheap.',
    '贵': 'This is too expensive!',
    '远': 'School is far from home.',
    '近': 'The park is close to my house.',
    '高': 'That tree is so tall!',
    '矮': "I'm shorter than my brother.",
    '长': 'This road is very long.',
    '短': 'My pencil is too short.',
    '宽': 'This road is very wide.',
    '窄': 'This alley is very narrow.',
    '干净': 'Your room is very clean.',
    '脏': 'Your hands are dirty, go wash them!',
    '漂亮': 'This flower is so pretty!',
    '好看': 'These clothes look nice.',
    '快乐': 'Happy birthday to you!',
    // Daily
    '网络': 'My house has internet.',
    '手机': "Don't keep looking at your phone.",
    '电视': "I'm watching TV.",
    '电话': 'Mom is on the phone.',
    '衣服': 'Wear this shirt today.',
    '裤子': 'I bought new pants.',
    '鞋': 'Put your shoes on properly.',
    '桌子': 'Put it on the table.',
    '椅子': 'Please sit on the chair.',
    '床': 'I go to bed at nine.',
    '门': 'Please close the door.',
    '窗': 'Please open the window.',
    '车': 'Dad drives me to school.',
    '出租车': "Let's take a taxi.",
    '飞机': "I've been on an airplane.",
    '票': 'I bought movie tickets.',
    '钱': "I've saved a lot of money.",
    '礼物': "This is a birthday gift for you.",
    '事情': 'I have things to do.',
    '意思': 'What does this word mean?',
    '旁边': "There's a supermarket next to the school.",
    '前面': "There's a park in front of my house.",
    '后面': 'The cat is hiding behind the sofa.',
    '左边': 'The school is on the left.',
    '右边': 'The hospital is on the right.',
    '睡觉': 'I go to sleep at nine.',
    '起床': 'I get up at six every day.',
    '上班': 'Dad went to work.',
    '洗': 'I washed my hands.',
    '打电话': "I'll call Mom.",
    '跑步': 'I run every morning.',
    '踢足球': 'We play soccer after school.',
    '打篮球': 'I like playing basketball.',
    '运动': 'You should exercise every day.',
    '跳舞': 'My sister likes dancing.',
    '唱歌': 'I like singing.',
    '旅游': "We're going traveling during the holiday.",
    '医生': 'I want to be a doctor.',
    '服务员': 'Waiter, the menu please.',
    '东西': 'Where are my things?',
    '电影': "Let's go watch a movie!",
    '公共汽车': 'I take the bus to school.',
    '火车': "I've been on a train.",
    '自行车': 'I can ride a bicycle.',
    '船': 'We crossed the river by boat.',
    '报纸': 'Grandpa is reading the newspaper.',
    '药': 'Take medicine when you are sick.',
    '杯子': 'Please give me a cup.',
    '生日': "Today is my birthday!",
    '谁': 'Who is at the door?',
    '什么': 'What are you doing?',
    '怎么': 'How do you write this word?',
    '怎么样': 'How have you been?',
    '为什么': 'Why is the sky blue?',
    '和': "I'm going with my friend.",
    '但是': "I want to go, but it's raining.",
    '因为': "Because it's raining, we can't go.",
    '所以': "I'm tired, so I'll rest a bit.",
    '从': 'I walk from home to school.',
    '向': 'Walk forward one hundred meters.',
    '离': 'School is not far from home.',
    '比': "I'm taller than my brother.",
    '很': "It's very cold today.",
    '非常': "I'm extremely happy!",
    '太': "That's great!",
    '最': 'This is my favorite book.',
    '真': "You're really awesome!",
    '都': 'We are all good friends.',
    '不': "I don't know.",
    '没': "I didn't bring an umbrella.",
    '每': 'Be happy every day.',
    '也': 'I want to go too.',
    '还': "I haven't eaten yet.",
    '再': 'Goodbye! Come again next time.',
    '就': "I'm coming!",
    '别': "Don't run too fast!",
    '已经': "I've already finished.",
    '一起': "Let's play together!",
    '可能': 'It might rain tomorrow.',
    '正在': "I'm doing my homework right now.",
    '一点': 'Please slow down a little.',
    '的': 'This is my book.',
    '得': 'He runs very fast.',
    '了': "I've eaten already.",
    '着': 'He is reading a book.',
    '过': "I've been to Beijing.",
    '吗': 'Are you a student?',
    '呢': 'What about you?',
    '吧': "Let's go!",
    // Appended
    '喂': 'Hello? Who is this?',
    '本': 'This book is very interesting.',
    '件': 'I bought a piece of clothing.',
    '张': 'Please give me a piece of paper.',
    '次': "This is my first time here.",
    '公斤': 'I bought two kilograms of apples.',
    '些': 'Give me some fruit, please.',
  }
  if (examples[chinese]) return examples[chinese]
  // Smarter fallback based on english meaning
  if (english.startsWith('to ')) return `I like to ${english.slice(3)}.`
  if (english.includes('color') || english.includes('Colour')) return `My favorite color is ${english}.`
  return `I like ${english}.`
}

function getMemoryTip(chinese: string, english: string): string {
  const tips: Record<string, string> = {
    // Greetings
    '你好': '你 (you) + 好 (good) = "You good?" = Hello!',
    '再见': '再 (again) + 见 (see) = "See you again" = Goodbye!',
    '谢谢': 'Sounds like "shyeh shyeh" - double thanks for emphasis!',
    '不客气': '不 (not) + 客气 (polite/formal) = "Don\'t be formal" = You\'re welcome',
    '对不起': '对 (correct) + 不 (not) + 起 (rise) = "Can\'t face you properly" = Sorry',
    '没关系': '没 (not) + 关系 (matter) = "No matter" = It\'s okay',
    '请': 'Looks like 言 (speech) + 青 (green/young) = "Speak gently" = Please',
    '早上': '早 (early) + 上 (up) = "Early up" = Morning',
    '晚上': '晚 (late) + 上 (up) = "Late part" = Evening',
    '欢迎': '欢 (happy) + 迎 (greet) = "Happy to greet you" = Welcome!',
    '认识': '认 (recognize) + 识 (know) = To know/recognize someone',
    '高兴': '高 (high) + 兴 (excitement) = "High excitement" = Happy!',
    '名字': '名 (name) + 字 (character) = Name characters = Name',
    '先生': '先 (first) + 生 (born) = "First born/senior" = Mr./Sir',
    '小姐': '小 (small/young) + 姐 (sister) = "Young lady" = Miss',
    // Numbers
    '一': 'One horizontal stroke — the simplest character! 一',
    '二': 'Two horizontal strokes stacked = 2',
    '三': 'Three horizontal strokes stacked = 3',
    '四': '四 looks like a window with 4 panes inside the box',
    '五': '五 has strokes going up-across-down like a hand showing 5',
    '六': '六 has a "lid" 亠 on top with two "legs" = 6',
    '七': '七 looks like an upside-down 7!',
    '八': '八 looks like two strokes splitting apart = 8',
    '九': '九 looks like an arm bent at the elbow, 9th position',
    '十': '十 is a cross shape (+) = 10, the base of counting',
    '百': '一 (one) on top of 白 (white) = 100, a "clean hundred"',
    '千': '千 looks like 十 (ten) with an extra stroke = a thousand',
    '万': '万 is simplified from 萬; think "10,000" — a myriad!',
    '零': '零 has 雨 (rain) on top — raindrops falling to zero',
    '两': '两 means "two" for counting things, has two 人 (people) inside',
    '第一': '第 (ordinal) + 一 (one) = 1st place!',
    '第二': '第 (ordinal) + 二 (two) = 2nd place!',
    '多少': '多 (many) + 少 (few) = "How many or how few?" = How many?',
    '几': '几 looks like a small table — "how many things on the table?"',
    '半': '半 has a vertical line splitting 八 in half = Half',
    '双': '双 has two 又 (again) = "Double/pair"',
    '个': '个 is the most common counter word — like "piece" or "unit"',
    '岁': '岁 (age) — "How many 岁 are you?" = How old?',
    '元': '元 = yuan, Chinese money. Think "original/primary currency"',
    '块': '块 = colloquial "kuai" for money, also means "piece/chunk"',
    // Family
    '爸爸': 'Babies everywhere say "baba" for dad — universal sound!',
    '妈妈': 'Babies everywhere say "mama" for mom — universal sound!',
    '哥哥': '"Gege" — repeated sound, like calling "bro bro!"',
    '姐姐': '"Jiejie" — 女 (woman) radical + 且 (older) = Older sister',
    '弟弟': '"Didi" — 弓 (bow) + 丿 (stroke) = Younger brother bows to elders',
    '妹妹': '"Meimei" — 女 (woman) + 未 (not yet) = "Not yet grown" = Younger sister',
    '爷爷': '"Yeye" — 父 (father) + 卩 = Grandfather (father\'s father)',
    '奶奶': '"Nainai" — 女 (woman) + 乃 = Grandmother',
    '叔叔': '"Shushu" — uncle on father\'s side',
    '阿姨': '"Ayi" — aunt, also used for any older woman politely',
    '儿子': '儿 (child) + 子 (son) = Son',
    '女儿': '女 (female) + 儿 (child) = Daughter',
    '孩子': '孩 (child) + 子 (little one) = Child/kid',
    '家': '家 has 宀 (roof) + 豕 (pig) = A pig under a roof means "home" (pigs = wealth)',
    '外公': '外 (outside/maternal) + 公 (grandpa) = Maternal grandpa',
    '外婆': '外 (outside/maternal) + 婆 (grandma) = Maternal grandma',
    '朋友': '朋 (two moons together) + 友 (hand helping hand) = Friend',
    '家人': '家 (home) + 人 (person) = "Home people" = Family member',
    '邻居': '邻 (adjacent) + 居 (live) = "Live next to" = Neighbor',
    '人': '人 looks like a walking person with two legs!',
    // Food
    '饭': '饣(food radical) + 反 = Rice/meal — the most common food!',
    '米饭': '米 (rice grain) + 饭 (meal) = Cooked rice',
    '面条': '面 (flour/face) + 条 (strip) = "Flour strips" = Noodles',
    '饺子': '饣(food) + 交 (cross/fold) = Dumplings — folded food!',
    '面包': '面 (flour) + 包 (wrap) = "Flour wrapped" = Bread',
    '鸡蛋': '鸡 (chicken) + 蛋 (egg) = Chicken egg',
    '肉': '肉 looks like strips of meat hanging — Meat!',
    '牛肉': '牛 (cow) + 肉 (meat) = Beef',
    '鸡肉': '鸡 (chicken) + 肉 (meat) = Chicken meat',
    '鱼': '鱼 looks like a fish with head, body, and tail!',
    '菜': '艹 (grass/plant) + 采 (pick) = Pick from plants = Vegetable',
    '水果': '水 (water) + 果 (fruit) = Fruit (needs water to grow)',
    '苹果': '苹 (apple tree) + 果 (fruit) = Apple',
    '香蕉': '香 (fragrant) + 蕉 (banana plant) = Banana',
    '西瓜': '西 (west) + 瓜 (melon) = "Western melon" = Watermelon',
    '茶': '艹 (plant) + 人 (person) + 木 (tree) = Tea — a person between plant and tree',
    '咖啡': 'Sounds like "ka fei" = "coffee" — a loanword!',
    '水': '水 looks like flowing water with splashes on the sides',
    '牛奶': '牛 (cow) + 奶 (milk/breast) = Cow milk',
    '果汁': '果 (fruit) + 汁 (juice) = Fruit juice',
    '可乐': '可 (can) + 乐 (joy/happy) = "Can be happy" = Cola!',
    '好吃': '好 (good) + 吃 (eat) = "Good eat" = Delicious!',
    '甜': '甜 has 甘 (sweet) + 舌 (tongue) = Sweet on the tongue!',
    '酸': '酉 (alcohol jar) + 夋 = Sour — like vinegar from a jar!',
    '辣': '辛 (spicy radical) + 束 = Spicy! Burns your mouth!',
    '咸': '咸 = Salty — think "all (咸 = all) food needs salt"',
    '饿': '饣(food) + 我 (me) = "My food!" = Hungry!',
    '渴': '氵(water) + 曷 = Need water = Thirsty!',
    '吃': '口 (mouth) + 乞 (beg) = "Mouth begging" = To eat',
    '喝': '口 (mouth) + 曷 = Put to mouth = To drink',
    // Animals
    '狗': '犭(animal) + 句 = Dog — the animal radical tells you!',
    '猫': '犭(animal) + 苗 (sprout/young) = Cat — "māo" sounds like "meow"!',
    '鸟': '鸟 looks like a bird sitting with a tail feather!',
    '马': '马 is simplified from a horse galloping — see the mane!',
    '牛': '牛 looks like a cow\'s face with two horns on top!',
    '羊': '羊 looks like a sheep face with two horns = Sheep/goat',
    '猪': '犭(animal) + 者 = Pig — an important farm animal',
    '鸡': '又 + 鸟 (bird) = Chicken — a domestic bird',
    '鸭': '甲 + 鸟 (bird) = Duck — another domestic bird',
    '兔': '兔 looks like a rabbit with long ears and a short tail!',
    '老虎': '老 (old/great) + 虎 (tiger) = Tiger — the great cat!',
    '大象': '大 (big) + 象 (elephant) = Big elephant!',
    '猴子': '犭(animal) + 侯 = Monkey — a clever animal',
    '蛇': '虫 (insect/reptile) + 它 = Snake — a "crawling creature"',
    '虫': '虫 originally depicted a snake/worm — now means insect/bug',
    '熊猫': '熊 (bear) + 猫 (cat) = "Bear cat" = Panda!',
    '动物': '动 (move) + 物 (thing) = "Moving thing" = Animal',
    '宠物': '宠 (favor/pet) + 物 (thing) = "Favored thing" = Pet',
    '龙': '龙 is the mighty Chinese dragon — symbol of power!',
    // Colors
    '颜色': '颜 (face) + 色 (color) = "Face color" = Color',
    '红': '纟(silk) + 工 (work) = Red — the color of luck in China!',
    '蓝': '艹 (plant) + 监 = Blue — like indigo dye from plants',
    '绿': '纟(silk) + 录 = Green — green silk thread',
    '黄': '黄 = Yellow — the color of the Yellow River & emperor',
    '白': '白 = White — a blank, pure canvas',
    '黑': '黑 = Black — soot marks from fire at the bottom',
    '橙': '木 (tree) + 登 = Orange — the orange tree fruit color',
    '紫': '此 (this) + 糸 (silk) = Purple — rare, royal silk color',
    '粉红': '粉 (powder) + 红 (red) = "Powdery red" = Pink!',
    '灰': '灰 = Gray — ash color, 火 (fire) turns things gray',
    '棕色': '棕 (palm tree) + 色 (color) = Brown — palm tree bark color',
    '金色': '金 (gold/metal) + 色 (color) = Gold color',
    '银色': '银 (silver) + 色 (color) = Silver color',
    '深': '氵(water) + 深 = Deep — deep water is dark colored',
    // Body
    '身体': '身 (body) + 体 (form) = Physical body',
    '头': '头 = Head — the top of everything!',
    '眼睛': '目 (eye radical) appears in both characters = Eyes!',
    '耳朵': '耳 (ear) + 朵 (measure word) = Ear',
    '鼻子': '鼻 is one of the most complex characters — Nose!',
    '嘴': '口 (mouth) + 此 + 角 = Mouth/lips',
    '手': '手 looks like a hand with fingers spread = Hand!',
    '脚': '月 (body part) + 却 = Foot',
    '腿': '月 (body part) + 退 = Leg — legs that carry you',
    '胳膊': '月 (body part radical) in both = Arm',
    '头发': '头 (head) + 发 (grow/emit) = "Head growth" = Hair',
    '脸': '月 (body) + 佥 = Face',
    '牙': '牙 looks like a molar tooth shape = Tooth!',
    '心': '心 looks like a heart with arteries = Heart/mind!',
    '生病': '生 (grow) + 病 (illness) = "Grow sick" = Get sick',
    // Time
    '时间': '时 (time) + 间 (between) = "Between moments" = Time',
    '今天': '今 (now) + 天 (day) = "Now day" = Today',
    '明天': '明 (bright) + 天 (day) = "Bright day" = Tomorrow (a new bright day!)',
    '昨天': '昨 (yesterday) + 天 (day) = Yesterday',
    '现在': '现 (appear) + 在 (at) = "Appearing at" = Now',
    '以前': '以 (with) + 前 (front) = "In front of now" = Before',
    '以后': '以 (with) + 后 (behind) = "Behind now" = After/later',
    '年': '年 = Year — originally meant "harvest season"',
    '月': '月 looks like a crescent moon = Month/Moon',
    '日': '日 looks like the sun ☀️ with a line = Day/Sun',
    '星期': '星 (star) + 期 (period) = "Star period" = Week',
    '周末': '周 (cycle) + 末 (end) = "End of the cycle" = Weekend!',
    '点': '点 = O\'clock/point — dots on a clock face',
    '分': '分 = Minute — 八 (eight/split) + 刀 (knife) = Divide time',
    '秒': '禾 (grain) + 少 (small) = Second — the smallest grain of time',
    '小时': '小 (small) + 时 (time) = "Small time" = Hour',
    '开始': '开 (open) + 始 (begin) = "Open the beginning" = Start!',
    '结束': '结 (tie) + 束 (bind) = "Tie it up" = End/finish',
    '快': '忄(heart) + 夬 = Fast — your heart beats fast!',
    '慢': '忄(heart) + 曼 = Slow — a relaxed heart',
    // Weather
    '天气': '天 (sky) + 气 (air/qi) = "Sky air" = Weather',
    '晴天': '日 (sun) + 青 (clear) = Sunny/clear day!',
    '阴天': '阴 (shade/yin) + 天 (day) = Cloudy day',
    '下雨': '下 (fall) + 雨 (rain) = "Rain falls" = It\'s raining',
    '下雪': '下 (fall) + 雪 (snow) = "Snow falls" = It\'s snowing',
    '风': '风 = Wind — the inner 㐅 swirls like wind!',
    '云': '云 = Cloud — simplified, looks soft and puffy',
    '太阳': '太 (great) + 阳 (yang/sun) = "Great sun" = Sun!',
    '月亮': '月 (moon) + 亮 (bright) = "Bright moon" = Moon!',
    '星星': 'Double 星 = Stars twinkling in the sky!',
    '热': '热 = Hot — 丸 (ball of) + 火 (fire) at bottom!',
    '冷': '冫(ice) + 令 = Cold — the ice radical says it all!',
    '暖': '日 (sun) + 爰 = Warm — sun warming you up',
    '凉': '冫(ice) + 京 = Cool — a gentle chill',
    '温度': '温 (warm) + 度 (degree) = Temperature',
    // School
    '学校': '学 (learn) + 校 (school building) = School!',
    '老师': '老 (old/experienced) + 师 (master) = "Experienced master" = Teacher',
    '学生': '学 (learn) + 生 (life/person) = "Learning person" = Student',
    '同学': '同 (same) + 学 (study) = "Study together" = Classmate',
    '教室': '教 (teach) + 室 (room) = "Teaching room" = Classroom',
    '书': '书 = Book — simplified from a hand holding a pen & paper',
    '本子': '本 (root/base) + 子 = Notebook — a basic writing book',
    '笔': '竹 (bamboo) + 毛 (hair) = Pen — originally a bamboo brush!',
    '字典': '字 (character) + 典 (classic) = "Classic of characters" = Dictionary',
    '电脑': '电 (electric) + 脑 (brain) = "Electric brain" = Computer!',
    '作业': '作 (make) + 业 (work) = "Make work" = Homework',
    '考试': '考 (test) + 试 (try) = "Test/try" = Exam',
    '问题': '问 (ask) + 题 (topic) = "Ask about a topic" = Question',
    '回答': '回 (return) + 答 (answer) = "Return an answer" = To answer',
    '学习': '学 (learn) + 习 (practice) = "Learn and practice" = Study',
    '教': '孝 (filial piety) + 攵 (action) = To teach',
    '懂': '忄(heart) + 董 = "Heart understands" = To understand',
    '容易': '容 (contain) + 易 (easy/change) = Easy',
    '难': '难 = Difficult — 又 (again) + 隹 (bird) = hard to catch a bird!',
    '练习': '练 (drill) + 习 (practice) = Practice/exercise',
    // Places
    '地方': '地 (ground) + 方 (direction) = Place',
    '厕所': '厕 (toilet) + 所 (place) = "Toilet place" = Bathroom',
    '房间': '房 (house) + 间 (between/room) = Room',
    '公司': '公 (public) + 司 (manage) = "Publicly managed" = Company',
    '商店': '商 (commerce) + 店 (shop) = Shop/store',
    '超市': '超 (super) + 市 (market) = Supermarket!',
    '医院': '医 (medicine) + 院 (courtyard/institution) = Hospital',
    '银行': '银 (silver) + 行 (go/firm) = "Silver firm" = Bank',
    '饭店': '饭 (rice/meal) + 店 (shop) = "Meal shop" = Restaurant',
    '机场': '机 (machine) + 场 (field) = "Machine field" = Airport',
    '车站': '车 (vehicle) + 站 (stand/stop) = "Vehicle stop" = Station',
    '公园': '公 (public) + 园 (garden) = "Public garden" = Park',
    '图书馆': '图 (picture) + 书 (book) + 馆 (hall) = "Book hall" = Library',
    '办公室': '办 (handle) + 公 (public) + 室 (room) = "Public work room" = Office',
    '城市': '城 (city wall) + 市 (market) = City',
    '国家': '国 (country) + 家 (home) = "Home country" = Country/nation',
    '中国': '中 (middle) + 国 (country) = "Middle Kingdom" = China!',
    '北京': '北 (north) + 京 (capital) = "Northern Capital" = Beijing!',
    '上海': '上 (on/above) + 海 (sea) = "On the sea" = Shanghai!',
    '泰国': '泰 (great/peaceful) + 国 (country) = Thailand!',
    // Verbs
    '做': '亻(person) + 故 = A person doing something = To do',
    '看': '手 (hand) + 目 (eye) = "Hand over eyes" = To look/see!',
    '听': '口 (mouth) + 斤 = Listen with your ear to someone\'s mouth = To listen',
    '说': '讠(speech) + 兑 = "Speech exchange" = To speak/say',
    '写': '写 = To write — putting pen to paper',
    '读': '讠(speech) + 卖 = "Voice out" = To read aloud',
    '走': '走 looks like a person walking = To walk!',
    '跑': '足 (foot) + 包 = "Feet wrapping around" = To run!',
    '坐': '坐 looks like two people sitting on the ground = To sit',
    '站': '立 (stand) + 占 = "Stand and occupy space" = To stand',
    '睡': '目 (eye) + 垂 (droop) = "Eyes drooping" = To sleep!',
    '起': '走 (walk) + 己 (self) = "Self rises up" = To get up',
    '开': '开 = To open — two hands pulling a gate open',
    '关': '关 = To close — the gate is shut',
    '买': '买 = To buy — simplified from a net catching money',
    '卖': '卖 = To sell — 买 (buy) with a hat on = the seller!',
    '送': '关 + 辶 (walk) = "Walk to deliver" = To give/send',
    '拿': '合 (combine) + 手 (hand) = "Hands combine to hold" = To take',
    '放': '方 (direction) + 攵 (action) = "Direct action" = To put/place',
    '找': '扌(hand) + 戈 (weapon) = "Hand searching" = To find/look for',
    '等': '竹 (bamboo) + 寺 (temple) = "Wait at the temple" = To wait',
    '玩': '王 (king) + 元 = "King enjoys" = To play!',
    '工作': '工 (work) + 作 (make) = To work',
    '休息': '亻(person) + 木 (tree) = "Person resting under tree" = To rest!',
    '帮助': '帮 (help) + 助 (assist) = To help',
    '告诉': '告 (tell) + 诉 (inform) = To tell someone',
    '知道': '知 (know) + 道 (way/path) = "Know the way" = To know',
    '会': '会 = Can/will — "come together" to know how',
    '想': '相 (mutual) + 心 (heart) = "Heart thinking" = To think/want',
    '要': '要 = To want/need — 西 (west) + 女 (woman) = "Want"',
    '爱': '爱 has 心 (heart) inside = Love comes from the heart!',
    '喜欢': '喜 (happiness) + 欢 (joyful) = "Joy + happiness" = To like!',
    '去': '去 = To go — the top means "earth" and bottom means "leave"',
    '来': '来 = To come — looks like wheat (麦) coming from the field',
    '住': '亻(person) + 主 (master) = "Person masters a place" = To live',
    '游泳': '氵(water) + 游 (swim) + 泳 (swim) = Swimming in water!',
    '飞': '飞 looks like a bird spreading wings = To fly!',
    // Adjectives
    '大': '大 looks like a person with arms spread wide = Big!',
    '小': '小 looks like a tiny thing with dots = Small',
    '多': 'Two 夕 (evening) stacked = "Many evenings" = Many/much',
    '少': '小 (small) with a stroke removed = Few/little',
    '新': '亲 (close) + 斤 (axe) = "Cut fresh" = New!',
    '旧': '旧 = Old/used — the opposite of 新 (new)',
    '好': '女 (woman) + 子 (child) = "Woman and child" = Good!',
    '坏': '坏 = Bad — earth (土) falling apart',
    '对': '对 = Correct — things properly "aligned"',
    '错': '错 = Wrong — 钅(metal) + 昔 (past) = "Metal gone wrong"',
    '忙': '忄(heart) + 亡 (die/lost) = "Heart lost" = Busy!',
    '累': '累 = Tired — heavy things piled up, exhausting!',
    '便宜': '便 (convenient) + 宜 (suitable) = Cheap/affordable',
    '贵': '贝 (shell/money) + 一 = "Above money" = Expensive!',
    '远': '远 = Far — 辶 (walk) + 元 = "Walk far" = Far',
    '近': '近 = Near — 辶 (walk) + 斤 = "Short walk" = Near',
    '高': '高 looks like a tall tower/building = Tall/high!',
    '矮': '矮 = Short — 矢 (arrow) + 委 = shorter than an arrow',
    '长': '长 = Long — think of a long flowing river',
    '短': '短 = Short — 矢 (arrow) + 豆 (bean) = short as a bean',
    '宽': '宽 = Wide — 宀 (roof) covers a wide space',
    '窄': '窄 = Narrow — 穴 (hole) + 乍 = narrow as a hole',
    '干净': '干 (dry) + 净 (clean) = "Dry and clean" = Clean!',
    '脏': '脏 = Dirty — 月 (body) + 庄 = body gets dirty',
    '漂亮': '漂 (float/bleach) + 亮 (bright) = "Bright and light" = Beautiful!',
    '好看': '好 (good) + 看 (look) = "Good to look at" = Good-looking!',
    // Daily
    '网络': '网 (net) + 络 (connect) = "Connected net" = Internet',
    '手机': '手 (hand) + 机 (machine) = "Hand machine" = Mobile phone!',
    '电视': '电 (electric) + 视 (view) = "Electric view" = TV!',
    '电话': '电 (electric) + 话 (speech) = "Electric speech" = Telephone!',
    '衣服': '衣 (clothing) + 服 (wear) = Clothes',
    '裤子': '衤(clothing) + 库 = Pants',
    '鞋': '革 (leather) + 圭 = Shoes — made from leather',
    '桌子': '桌 (table) + 子 = Table — flat surface for work',
    '椅子': '木 (wood) + 奇 (strange) + 子 = Chair — wooden seat',
    '床': '广 (shelter) + 木 (wood) = "Wood under shelter" = Bed',
    '门': '门 looks like a door with two panels = Door!',
    '窗': '穴 (hole) + 囱 = "Hole in the wall" = Window',
    '车': '车 originally looked like a chariot from above = Car/vehicle',
    '出租车': '出 (go out) + 租 (rent) + 车 (car) = "Rent-out car" = Taxi!',
    '飞机': '飞 (fly) + 机 (machine) = "Flying machine" = Airplane!',
    '票': '票 = Ticket — 西 + 示 = "Show to pass"',
    '钱': '钅(metal) + 戋 = Money — originally metal coins!',
    '礼物': '礼 (ceremony/gift) + 物 (thing) = Gift',
    '事情': '事 (matter) + 情 (feeling) = "Matter/affair"',
    '意思': '意 (idea) + 思 (think) = Meaning',
    '旁边': '旁 (side) + 边 (edge) = Beside',
    '前面': '前 (front) + 面 (face/side) = In front',
    '后面': '后 (behind) + 面 (face/side) = Behind',
    '左边': '左 (left) + 边 (side) = Left side — 工 (work) + 手 (hand)',
    '右边': '右 (right) + 边 (side) = Right side — 口 (mouth) + 手 (hand)',
  }
  return tips[chinese] || `Remember: "${chinese}" means "${english}"`
}

function getParts(chinese: string): string {
  if (chinese.length === 1) return `${chinese} (single character)`
  return chinese.split('').join(' + ')
}


export default function LearnMode() {
  const navigate = useNavigate()
  const [words, setWords] = useState<VocabType[]>([])
  const [currentIndex, setCurrentIndex] = useState(0)
  const [viewedWords, setViewedWords] = useState<Set<number>>(new Set([0]))
  const [showExportModal, setShowExportModal] = useState(false)
  const [exportImage, setExportImage] = useState<string>('')
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const isDrawingRef = useRef(false)
  const lastPosRef = useRef({ x: 0, y: 0 })

  useEffect(() => {
    const stored = sessionStorage.getItem('selectedCategories')
    if (stored) {
      const cats = JSON.parse(stored)
      const filteredWords = vocabularyWithLearning.filter(w => cats.includes(w.category))
      setWords(filteredWords)
    } else {
      navigate('/')
    }
  }, [navigate])

  const currentWord = words[currentIndex]
  const allViewed = viewedWords.size >= words.length

  // Canvas drawing handlers
  const getCanvasPos = useCallback((e: React.TouchEvent | React.MouseEvent) => {
    const canvas = canvasRef.current
    if (!canvas) return { x: 0, y: 0 }
    const rect = canvas.getBoundingClientRect()
    const scaleX = canvas.width / rect.width
    const scaleY = canvas.height / rect.height
    if ('touches' in e) {
      const touch = e.touches[0] || e.changedTouches[0]
      return { x: (touch.clientX - rect.left) * scaleX, y: (touch.clientY - rect.top) * scaleY }
    }
    return { x: (e.clientX - rect.left) * scaleX, y: (e.clientY - rect.top) * scaleY }
  }, [])

  const startDraw = useCallback((e: React.TouchEvent | React.MouseEvent) => {
    e.preventDefault()
    isDrawingRef.current = true
    const pos = getCanvasPos(e)
    lastPosRef.current = pos
  }, [getCanvasPos])

  const draw = useCallback((e: React.TouchEvent | React.MouseEvent) => {
    e.preventDefault()
    if (!isDrawingRef.current) return
    const canvas = canvasRef.current
    const ctx = canvas?.getContext('2d')
    if (!ctx) return
    const pos = getCanvasPos(e)
    ctx.beginPath()
    ctx.moveTo(lastPosRef.current.x, lastPosRef.current.y)
    ctx.lineTo(pos.x, pos.y)
    ctx.strokeStyle = '#1a1a1a'
    ctx.lineWidth = 4
    ctx.lineCap = 'round'
    ctx.lineJoin = 'round'
    ctx.stroke()
    lastPosRef.current = pos
  }, [getCanvasPos])

  const stopDraw = useCallback(() => {
    isDrawingRef.current = false
  }, [])

  const clearCanvas = useCallback(() => {
    const canvas = canvasRef.current
    const ctx = canvas?.getContext('2d')
    if (!ctx || !canvas) return
    ctx.clearRect(0, 0, canvas.width, canvas.height)
  }, [])

  const handleExport = useCallback(() => {
    const canvas = canvasRef.current
    if (!canvas) return
    const imageData = canvas.toDataURL('image/png')
    setExportImage(imageData)
    setShowExportModal(true)
  }, [])

  // Clear canvas when word changes
  useEffect(() => {
    clearCanvas()
  }, [currentIndex, clearCanvas])

  const speakWord = (text: string) => {
    const utterance = new SpeechSynthesisUtterance(text)
    utterance.lang = 'zh-CN'
    utterance.rate = 0.7
    speechSynthesis.speak(utterance)
  }

  const speakSentence = (text: string) => {
    const utterance = new SpeechSynthesisUtterance(text)
    utterance.lang = 'zh-CN'
    utterance.rate = 0.6
    speechSynthesis.speak(utterance)
  }

  const goNext = () => {
    if (currentIndex < words.length - 1) {
      const next = currentIndex + 1
      setCurrentIndex(next)
      setViewedWords(prev => new Set(prev).add(next))
    }
  }

  const goPrev = () => {
    if (currentIndex > 0) {
      setCurrentIndex(prev => prev - 1)
    }
  }

  const getCategoryName = (cat: Category) => {
    const found = categories.find(c => c.id === cat)
    return found ? `${found.icon} ${found.nameEn}` : cat
  }

  if (!currentWord) return null

  return (
    <div className="page-shell bg-gradient-to-br from-teal-500 via-cyan-500 to-blue-500 p-3 md:p-4 flex flex-col">
      <div className="max-w-4xl mx-auto w-full flex-1 flex flex-col min-h-0">
        {/* Header */}
        <div className="flex items-center justify-between mb-3 flex-shrink-0 gap-2 flex-wrap">
          <Link 
            to="/" 
            className="bg-white/20 rounded-2xl p-2.5 md:p-3 active:scale-95 transition-transform"
          >
            <ArrowLeft className="w-6 h-6 md:w-8 md:h-8 text-white" />
          </Link>
          
          <div className="flex items-center gap-2 md:gap-3 flex-wrap justify-end">
            <div className="bg-white/20 rounded-2xl px-3 py-1.5 md:px-4 md:py-2">
              <span className="text-white font-bold text-base md:text-lg">{currentIndex + 1}/{words.length}</span>
            </div>
            <div className="bg-white/20 rounded-2xl px-3 py-1.5 md:py-2 max-w-[52vw] sm:max-w-none">
              <span className="text-white font-bold text-xs md:text-sm truncate block">{getCategoryName(currentWord.category)}</span>
            </div>
            {allViewed && (
              <Link
                to="/"
                className="bg-red-500 hover:bg-red-600 rounded-2xl px-3 py-1.5 md:px-4 md:py-2 flex items-center gap-1.5
                           active:scale-95 transition-all"
              >
                <LogOut className="w-4 h-4 text-white" />
                <span className="text-white font-bold text-sm">End</span>
              </Link>
            )}
          </div>
        </div>

        {/* Main Content */}
        <div className="flex-1 flex flex-col lg:flex-row gap-3 min-h-0">
          {/* Left: Image + Word + Audio */}
          <div className="w-full lg:w-2/5 bg-white rounded-3xl shadow-2xl flex flex-col p-4">
            <div className="bg-gradient-to-br from-gray-100 to-gray-200 rounded-2xl flex items-center justify-center p-4"
                 style={{ minHeight: '140px' }}>
              <img 
                src={getWordImageUrl(currentWord.id)}
                data-fallback-emoji={getEmojiUrl(currentWord.chinese)}
                alt=""
                className="w-36 h-36 object-contain rounded-xl"
                onError={(e) => {
                  const el = e.target as HTMLImageElement
                  const fb = el.dataset.fallbackEmoji
                  if (fb && el.src !== fb) { el.src = fb }
                }}
              />
            </div>
            <div className="text-center mt-4 flex-1 flex flex-col justify-center">
              <div className="chinese-text text-5xl md:text-7xl font-bold text-gray-800 leading-tight break-words">
                {currentWord.chinese}
              </div>
              <div className="text-xl text-gray-500 mt-2">{currentWord.pinyin}</div>
              <div className="text-xl font-semibold text-teal-600 mt-1">{currentWord.english}</div>
            </div>
            <button
              onClick={() => speakWord(currentWord.chinese)}
              className="w-full bg-gradient-to-r from-teal-500 to-cyan-500 text-white rounded-2xl py-3 
                         flex items-center justify-center gap-2 active:scale-95 transition-transform mt-4"
            >
              <Volume2 className="w-5 h-5" />
              <span className="font-bold text-base">Listen</span>
            </button>
          </div>

          {/* Right: Details */}
          <div className="flex-1 flex flex-col gap-2">
            {/* Character Breakdown */}
            <div className="bg-white rounded-2xl p-3 shadow-lg">
              <div className="flex items-center gap-2 mb-1">
                <BookOpen className="w-4 h-4 text-blue-600" />
                <span className="font-bold text-blue-800 text-sm">Character Breakdown</span>
              </div>
              <p className="text-blue-900 text-sm">{currentWord.parts}</p>
            </div>

            {/* Memory Tip */}
            <div className="bg-white rounded-2xl p-3 shadow-lg">
              <div className="flex items-center gap-2 mb-1">
                <Lightbulb className="w-4 h-4 text-yellow-600" />
                <span className="font-bold text-yellow-800 text-sm">Memory Tip</span>
              </div>
              <p className="text-yellow-900 text-sm">{currentWord.memoryTip}</p>
            </div>

            {/* Example Sentence */}
            <div className="bg-white rounded-2xl p-3 shadow-lg">
              <div className="flex items-center gap-2 mb-1">
                <MessageSquare className="w-4 h-4 text-green-600" />
                <span className="font-bold text-green-800 text-sm">Example Sentence</span>
              </div>
              <div className="flex items-start justify-between gap-2">
                <div className="flex-1">
                  <p className="text-green-900 chinese-text text-sm">{currentWord.example}</p>
                  <p className="text-green-700 text-xs mt-1">{currentWord.exampleEnglish}</p>
                </div>
                <button
                  onClick={() => speakSentence(currentWord.example || '')}
                  className="bg-green-200 p-2 rounded-xl active:scale-95 transition-transform flex-shrink-0"
                >
                  <Volume2 className="w-4 h-4 text-green-700" />
                </button>
              </div>
            </div>

            {/* Practice Writing Canvas */}
            <div className="flex-1 flex flex-col min-h-0">
              <div className="bg-white rounded-2xl p-3 shadow-lg flex-1 flex flex-col min-h-0">
                <div className="flex items-center justify-between mb-2 gap-2">
                  <div className="flex items-center gap-2 min-w-0 justify-start flex-1">
                    <BookOpen className="w-4 h-4 text-blue-600 shrink-0" />
                    <span className="font-bold text-blue-800 text-sm text-left truncate">Practice Writing</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <button onClick={handleExport} className="text-xs text-gray-400 flex items-center gap-1 active:scale-95">
                      <Download className="w-3 h-3" /> Export
                    </button>
                    <button onClick={clearCanvas} className="text-xs text-gray-400 flex items-center gap-1 active:scale-95">
                      <Eraser className="w-3 h-3" /> Clear
                    </button>
                  </div>
                </div>
                <div className="relative flex-1 min-h-[120px] rounded-xl border-2 border-dashed border-gray-300 overflow-hidden bg-white">
                  {/* Guide character - behind canvas */}
                  <div className="absolute inset-0 flex items-center justify-center pointer-events-none select-none z-0 px-1">
                    <span className="chinese-text text-[min(48vw,9rem)] md:text-[10rem] text-gray-300 font-bold leading-none max-w-full text-center tracking-tight scale-110">{currentWord.chinese}</span>
                  </div>
                  <canvas
                    ref={canvasRef}
                    width={300}
                    height={200}
                    className="relative z-10 w-full h-full cursor-crosshair touch-none"
                    onMouseDown={startDraw}
                    onMouseMove={draw}
                    onMouseUp={stopDraw}
                    onMouseLeave={stopDraw}
                    onTouchStart={startDraw}
                    onTouchMove={draw}
                    onTouchEnd={stopDraw}
                  />
                </div>
              </div>
            </div>

            {/* Navigation */}
            <div className="flex gap-3">
              <button
                onClick={goPrev}
                disabled={currentIndex === 0}
                className="flex-1 bg-white/20 text-white rounded-2xl py-3 
                           flex items-center justify-center gap-2 active:scale-95 
                           transition-transform disabled:opacity-30 disabled:cursor-not-allowed"
              >
                <ChevronLeft className="w-5 h-5" />
                <span className="font-bold">Previous</span>
              </button>
              {currentIndex === words.length - 1 ? (
                <Link
                  to="/"
                  className="flex-1 bg-red-500 text-white rounded-2xl py-3 
                             flex items-center justify-center gap-2 active:scale-95 transition-transform"
                >
                  <LogOut className="w-5 h-5" />
                  <span className="font-bold">End</span>
                </Link>
              ) : (
                <button
                  onClick={goNext}
                  className="flex-1 bg-teal-600 text-white rounded-2xl py-3 
                             flex items-center justify-center gap-2 active:scale-95 transition-transform"
                >
                  <span className="font-bold">Next</span>
                  <ChevronRight className="w-5 h-5" />
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Export Modal */}
      {showExportModal && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="relative">
            {/* Close button */}
            <button
              onClick={() => setShowExportModal(false)}
              className="absolute -top-12 -right-2 bg-white/20 rounded-full p-2 active:scale-95 transition-transform"
            >
              <X className="w-6 h-6 text-white" />
            </button>
            
            {/* Card */}
            <div className="bg-white rounded-3xl shadow-2xl overflow-hidden">
              {/* Handwritten character */}
              <div className="bg-gradient-to-br from-amber-50 to-orange-50 p-8 flex items-center justify-center">
                <img
                  src={exportImage}
                  alt="Your handwriting"
                  className="w-64 h-44 object-contain"
                />
              </div>
              
              {/* Pinyin and Meaning */}
              <div className="bg-gradient-to-r from-teal-500 to-cyan-500 p-4 text-center">
                <div className="chinese-text text-4xl font-bold text-white mb-1">{currentWord.chinese}</div>
                <div className="text-xl text-white/90 font-medium">{currentWord.pinyin}</div>
                <div className="text-lg text-yellow-200 font-semibold mt-1">{currentWord.english}</div>
              </div>
            </div>
            
            {/* Screenshot hint */}
            <p className="text-center text-white/70 text-sm mt-4">
              📸 Take a screenshot to save your handwriting card!
            </p>
          </div>
        </div>
      )}
    </div>
  )
}
